import { Site } from "@inrange/building-manager-api-client/models-site";
import {
  Column,
  Grid,
  SummaryBoxMetric,
  SummaryBoxMetrics,
} from "@inrange/theme-components";
import { OperationalSiteChart } from "@inrange/theme-components/charts";
import {
  formatLeaseTariff,
  formatLeaseTariffBillingFrequency,
  tariffToFractionalCurrency,
} from "@inrange/theme-components/formatting";
import React from "react";
import { getLatestActualsMonthIndex } from "../../views/sitelist-utils";
import {
  getOperationalEnergySoldChartData,
  getOperationalRevenueChartData,
} from "./chartDataFormatting";
import { generateRevenueMetrics, getTariffHeader } from "./operational-utils";
import OperationalDemoMetrics from "./OperationalDemoMetrics";

const OperationalSummaryLandlord: React.FC<{
  site: Site;
  isOwnerOccupier: boolean;
  orgDemo: boolean;
}> = ({ site, isOwnerOccupier, orgDemo }) => {
  const revenueChartData = getOperationalRevenueChartData(
    site,
    isOwnerOccupier,
    isOwnerOccupier && site.activeInvestmentModel === "license",
    site.activeInvestmentModel
  );
  const energySoldChartData = getOperationalEnergySoldChartData(
    site,
    isOwnerOccupier && site.activeInvestmentModel === "license"
  );

  const lastMonthIndex = getLatestActualsMonthIndex(site);
  const lastMonthEnergyActuals = lastMonthIndex
    ? site.energyFinancialsMonthly[lastMonthIndex].energy.actual
    : undefined;
  const lastMonthFinancialActuals = lastMonthIndex
    ? site.energyFinancialsMonthly[lastMonthIndex].financial.actual
    : undefined;
  const isOnsitePvLease =
    site.tariffConfig.onsitePPA.ppaPurchaseModel === "fixed_lease";
  const revenueMetrics = generateRevenueMetrics(
    site,
    lastMonthEnergyActuals,
    lastMonthFinancialActuals,
    isOwnerOccupier,
    isOnsitePvLease,
    site.activeInvestmentModel
  );
  const tariffMetrics: SummaryBoxMetric[] = [];
  if (isOnsitePvLease) {
    tariffMetrics.push({
      value: formatLeaseTariff(
        site.tariffConfig.onsitePPA.tariff,
        "annually",
        site.currencyCode
      ),
      valueSubText: formatLeaseTariffBillingFrequency("annually"),
      dataTestId: "test-tariff",
    });
  } else if (site.energyFlowAnnual.behindMeter > 0) {
    tariffMetrics.push({
      value: tariffToFractionalCurrency(
        site.tariffConfig.onsitePPA.tariff,
        site.currencyCode
      ),
      valueSubText: "On-site",
      dataTestId: "tenant-tariff",
    });
  }
  if (
    site.energyFlowAnnual.networkExport + site.energyFlowAnnual.exported >
    0
  ) {
    tariffMetrics.push({
      value: tariffToFractionalCurrency(
        site.blendedExportTariff,
        site.currencyCode
      ),
      valueSubText: "Avg export",
    });
  }
  const tariffHeader = getTariffHeader(isOnsitePvLease, tariffMetrics.length);
  const chartsHeight = "416px";

  return (
    <Grid $cols={1} $colwidth={"370px 1fr"} $gap={"10px"} $margin={"5px"}>
      <Column $height={"fit-content"}>
        <OperationalDemoMetrics site={site} orgDemo={orgDemo} />
        <SummaryBoxMetrics
          metrics={tariffMetrics}
          header={tariffHeader}
          grid={true}
        />
        {revenueMetrics.length > 0 && (
          <SummaryBoxMetrics
            metrics={revenueMetrics}
            header={"Sold previous month"}
            grid={false}
          />
        )}
      </Column>
      <Column $height={"100%"}>
        {!isOnsitePvLease && (
          <OperationalSiteChart
            header="Revenue"
            chartData={revenueChartData}
            toolTipText="The revenue generated from energy sold after the given revenue share during a given month, where energy can be sold on-site or exported to the InRange network, at the relevant tariffs. For months where no data exists, the data reflects the forecast revenue. Does not include VAT."
            dataType="currency"
            currencyCode={site.currencyCode}
            height={chartsHeight}
          />
        )}
        <OperationalSiteChart
          header="Energy sold by month"
          chartData={energySoldChartData}
          toolTipText="The amount of energy sold in kWh during a given month, where energy can be sold on-site or exported to the InRange network. For months where no data exists, the data reflects the forecast amount."
          dataType="energy"
          height={chartsHeight}
        />
      </Column>
    </Grid>
  );
};

export default OperationalSummaryLandlord;
